import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { CloserStatsDTO } from "../../HubSpot/useGetSchedulerCloserStats.ts";
import { classNames } from "../../UI/Utils/classNames.ts";
import { capSaturation, formatRate } from "./closerStatsFormat.ts";
import { closerDetailPath } from "../Detail/closerDetailPath.ts";

type Props = {
  closers: CloserStatsDTO[];
  selectedCloserId: string | null;
  onSelect: (closerId: string) => void;
};

type Column = {
  key: string;
  label: string;
  value: (closer: CloserStatsDTO) => number | null;
  render: (closer: CloserStatsDTO) => string;
};

const COLUMNS: Column[] = [
  {
    key: "currentCr",
    label: "CR attuale",
    value: (c) => c.currentCr,
    render: (c) =>
      c.currentCrWonDeals === null || c.currentCrCallsDone === null
        ? formatRate(c.currentCr)
        : `${formatRate(c.currentCr)} (${c.currentCrWonDeals}/${c.currentCrCallsDone})`,
  },
  {
    key: "callsDone",
    label: "Call effettuate",
    value: (c) => c.totals.callsDone,
    render: (c) => String(c.totals.callsDone),
  },
  {
    key: "wonDeals",
    label: "Deal vinti",
    value: (c) => c.totals.wonDeals,
    render: (c) => String(c.totals.wonDeals),
  },
  {
    key: "cr",
    label: "CR periodo",
    value: (c) => (c.totals.callsDone === 0 ? null : c.totals.cr),
    render: (c) => (c.totals.callsDone === 0 ? "—" : formatRate(c.totals.cr)),
  },
  {
    key: "noShow",
    label: "No show",
    value: (c) => c.totals.noShow,
    render: (c) => String(c.totals.noShow),
  },
  {
    key: "toReschedule",
    label: "Da rifissare",
    value: (c) => c.totals.toReschedule,
    render: (c) => String(c.totals.toReschedule),
  },
  {
    key: "booked",
    label: "Prenotate",
    value: (c) => c.totals.booked,
    render: (c) =>
      `${c.totals.booked} (${c.totals.bookedWeb}/${c.totals.bookedSetter})`,
  },
  {
    key: "cancelled",
    label: "Cancellate",
    value: (c) => c.totals.cancelled,
    render: (c) => String(c.totals.cancelled),
  },
  {
    key: "capacity",
    label: "Cap",
    value: (c) => c.capacity,
    render: (c) => (c.capacity === null ? "—" : String(c.capacity)),
  },
  {
    key: "saturation",
    label: "Saturazione",
    value: (c) => capSaturation(c.totals),
    render: (c) => formatRate(capSaturation(c.totals)),
  },
  {
    key: "daysAtCap",
    label: "Giorni al cap",
    value: (c) => c.totals.daysAtCap,
    render: (c) => `${c.totals.daysAtCap}/${c.totals.capacityDays}`,
  },
  {
    key: "callsOverCap",
    label: "Oltre il cap",
    value: (c) => c.totals.callsOverCap,
    render: (c) => String(c.totals.callsOverCap),
  },
];

function closerTags(closer: CloserStatsDTO): string[] {
  if (closer.exclusionReason) return [`Escluso: ${closer.exclusionReason}`];
  return [
    ...(closer.inTrial ? ["In prova"] : []),
    ...(closer.tierRank ? [`Tier ${closer.tierRank}`] : []),
    ...(!closer.acceptsWeb ? ["Solo setter"] : []),
    ...(!closer.acceptsSetter ? ["Solo landing"] : []),
  ];
}

function compareNullable(a: number | null, b: number | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a - b;
}

export function CloserStatsTable({
  closers,
  selectedCloserId,
  onSelect,
}: Props) {
  const navigate = useNavigate();
  const [sortKey, setSortKey] = useState<string>("cr");
  const [descending, setDescending] = useState(true);

  const column = COLUMNS.find((c) => c.key === sortKey);
  const sorted = [...closers].sort((a, b) => {
    if (!column) return a.name.localeCompare(b.name);
    const valueA = column.value(a);
    const valueB = column.value(b);
    if (valueA === null || valueB === null) {
      return compareNullable(valueA, valueB) || a.name.localeCompare(b.name);
    }
    const order = descending ? valueB - valueA : valueA - valueB;
    return order || a.name.localeCompare(b.name);
  });

  const onSort = (key: string) => {
    if (key === sortKey) {
      setDescending((d) => !d);
    } else {
      setSortKey(key);
      setDescending(true);
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
            <th className="py-2 pr-3 font-medium">Closer</th>
            {COLUMNS.map((c) => (
              <th key={c.key} className="py-2 px-2 font-medium text-right">
                <button
                  type="button"
                  onClick={() => onSort(c.key)}
                  className="inline-flex items-center gap-0.5 hover:text-gray-800"
                >
                  {c.label}
                  {sortKey === c.key &&
                    (descending ? (
                      <ChevronDownIcon className="w-3 h-3" />
                    ) : (
                      <ChevronUpIcon className="w-3 h-3" />
                    ))}
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((closer) => (
            <tr
              key={closer.closerId}
              onClick={() => onSelect(closer.closerId)}
              className={classNames(
                "border-b border-gray-100 cursor-pointer hover:bg-gray-50",
                closer.closerId === selectedCloserId ? "bg-blue-50" : "",
              )}
            >
              <td className="py-2 pr-3">
                <div className="flex items-center gap-2">
                  <p className="font-medium">{closer.name}</p>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(closerDetailPath(closer.closerId));
                    }}
                    className="text-xs text-primary hover:underline"
                  >
                    Scheda
                  </button>
                </div>
                <div className="flex flex-wrap gap-1 mt-0.5">
                  {closerTags(closer).map((tag) => (
                    <span
                      key={tag}
                      className={classNames(
                        "text-xs px-1.5 py-0.5 rounded",
                        closer.exclusionReason
                          ? "bg-red-100 text-red-700"
                          : "bg-gray-100 text-gray-600",
                      )}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </td>
              {COLUMNS.map((c) => (
                <td key={c.key} className="py-2 px-2 text-right tabular-nums">
                  {c.render(closer)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
